

const PRAGUE_LOCALE_CS = 'cs-CZ'
const PRAGUE_LOCALE_EN = 'en-GB'

type Locale = 'cs' | 'en'

function bcp(locale: Locale) {
  return locale === 'cs' ? PRAGUE_LOCALE_CS : PRAGUE_LOCALE_EN
}

export function formatCzk(value: number, locale: Locale = 'cs'): string {
  return new Intl.NumberFormat(bcp(locale), {
    style: 'currency',
    currency: 'CZK',
    maximumFractionDigits: 0,
  }).format(value)
}

export function formatCzkShort(value: number, locale: Locale = 'cs'): string {
  if (Math.abs(value) >= 1_000_000) {
    return `${new Intl.NumberFormat(bcp(locale), { maximumFractionDigits: 2 }).format(value / 1_000_000)} M Kč`
  }
  if (Math.abs(value) >= 10_000) {
    return `${new Intl.NumberFormat(bcp(locale), { maximumFractionDigits: 0 }).format(value / 1_000)} k Kč`
  }
  return formatCzk(value, locale)
}

export function formatNumber(value: number, locale: Locale = 'cs', max = 0): string {
  return new Intl.NumberFormat(bcp(locale), { maximumFractionDigits: max }).format(value)
}

export function formatPercent(value: number, locale: Locale = 'cs', digits = 1): string {
  return new Intl.NumberFormat(bcp(locale), {
    style: 'percent',
    maximumFractionDigits: digits,
    minimumFractionDigits: digits,
  }).format(value / 100)
}

export function formatDelta(value: number, locale: Locale = 'cs', digits = 1): string {
  const sign = value > 0 ? '+' : ''
  return `${sign}${new Intl.NumberFormat(bcp(locale), {
    maximumFractionDigits: digits,
    minimumFractionDigits: digits,
  }).format(value)} %`
}

export function formatDate(d: Date, locale: Locale = 'cs'): string {
  return new Intl.DateTimeFormat(bcp(locale), {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(d)
}

export function formatTime(d: Date, locale: Locale = 'cs'): string {
  return new Intl.DateTimeFormat(bcp(locale), {
    hour: '2-digit',
    minute: '2-digit',
  }).format(d)
}

export function formatTimeWithSeconds(d: Date, locale: Locale = 'cs'): string {
  return new Intl.DateTimeFormat(bcp(locale), {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).format(d)
}

export function formatRelativeAge(fromIso: string, locale: Locale = 'cs', refNow?: Date): string {
  const now = refNow ?? new Date()
  const then = new Date(fromIso)
  const diffMin = Math.max(0, Math.round((now.getTime() - then.getTime()) / 60_000))
  if (diffMin < 1) return locale === 'cs' ? 'právě teď' : 'just now'
  if (diffMin < 60) return locale === 'cs' ? `před ${diffMin} min` : `${diffMin} min ago`
  const hours = Math.floor(diffMin / 60)
  if (hours < 24) return locale === 'cs' ? `před ${hours} h` : `${hours} h ago`
  const days = Math.floor(hours / 24)
  return locale === 'cs' ? `před ${days} d` : `${days} d ago`
}
